/**
 * useRoleSwitch Hook
 * 
 * Manages mock role state for viewing the application as a different role.
 * Stores mock role ID in localStorage and provides methods to set/clear it.
 */

import { useState, useEffect, useCallback } from 'react';

const MOCK_ROLE_STORAGE_KEY = 'tracekit-mock-role-id';

export interface UseRoleSwitchReturn {
  mockRoleId: string | null;
  setMockRole: (roleId: string | null) => void;
  clearMockRole: () => void;
  isMockRoleActive: boolean;
}

export function useRoleSwitch(): UseRoleSwitchReturn {
  const [mockRoleId, setMockRoleId] = useState<string | null>(null);

  // Load mock role from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(MOCK_ROLE_STORAGE_KEY);
      if (stored) {
        setMockRoleId(stored);
      }
    } catch (error) {
      console.warn('[useRoleSwitch] Failed to load mock role from localStorage:', error);
    }
  }, []);

  // Set mock role
  const setMockRole = useCallback((roleId: string | null) => {
    try {
      if (roleId) {
        localStorage.setItem(MOCK_ROLE_STORAGE_KEY, roleId);
        setMockRoleId(roleId);
      } else {
        localStorage.removeItem(MOCK_ROLE_STORAGE_KEY);
        setMockRoleId(null);
      }
    } catch (error) {
      console.warn('[useRoleSwitch] Failed to save mock role to localStorage:', error);
    }
  }, []);

  // Clear mock role
  const clearMockRole = useCallback(() => {
    setMockRole(null);
  }, [setMockRole]);

  // Check if mock role is active
  const isMockRoleActive = mockRoleId !== null;

  return {
    mockRoleId,
    setMockRole,
    clearMockRole,
    isMockRoleActive,
  };
}

// Clear mock role on logout (call this from logout handler)
export function clearMockRoleOnLogout() {
  try {
    localStorage.removeItem(MOCK_ROLE_STORAGE_KEY);
  } catch (error) {
    console.warn('[useRoleSwitch] Failed to clear mock role on logout:', error);
  }
}


